import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import type { NormalizedApiError } from '@/shared/api';
import { ErrorState, LoadingState } from '@/shared/ui';

import {
  useCatalogLanguages,
  useCategory,
  useCategoryTranslations,
  useCreateCategory,
  useUpsertCategory,
} from '../../hooks';
import { wouldLikelyExceedMaxDepth } from '../../lib/categoryTree';
import type { ParentCategoryOption } from '../../lib/categoryTree';
import { useCardLanguageDraft } from '../../lib/useCardLanguageDraft';
import type { ToastSeverity } from '../../lib/useToast';
import type { CategoryTranslationInput, CategoryUpsertRequest } from '../../model/types';
import { CardLanguageSwitcher } from './CardLanguageSwitcher';

interface CategoryFormDialogProps {
  open: boolean;
  /** The id of the category being edited, or `null` to create a new one. */
  categoryId: string | null;
  /**
   * Eligible parent options (tree-flattened, own subtree already excluded by the caller — see
   * `buildParentOptions`).
   */
  parentOptions: ParentCategoryOption[];
  /** `catalog.structure.manage`-equivalent gate for the category tree (`catalog.manage`) — a
   * viewer can open the dialog to inspect a category, but every field is read-only and Save is
   * disabled. */
  canManage: boolean;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

const NO_PARENT_VALUE = '';

// ── Card-language switcher (epic-catalog-004, adr-tenant-005, plan-tenant-004 Wave 2) ─────────
// A module-level constant (not recreated per render) keeps `useCardLanguageDraft`'s internal
// memoization stable — see that hook's doc for the empty-registry single-language fallback.
interface CategoryLocaleFields {
  name: string;
  description: string;
}
const CATEGORY_LOCALE_EMPTY_FIELDS: CategoryLocaleFields = { name: '', description: '' };

function slugify(value: string): string {
  const map: Record<string, string> = {
    а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y',
    к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f',
    х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
  };
  return value
    .toLowerCase()
    .split('')
    .map((ch) => map[ch] ?? ch)
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Create / edit / move a catalog category via the catalog master API. Editing fetches the full
 * `CategoryDetail` by id (so the tree row — which only carries id/name/children — doesn't need to
 * pre-supply slug/code/imageUrl/parent). The parent selector re-parents the category on save
 * ("move" is just editing `parentCategoryId`); `CATEGORY_CYCLE` / `CATEGORY_DEPTH_EXCEEDED` are
 * surfaced as precise, translated errors instead of the raw backend message.
 */
export function CategoryFormDialog({
  open,
  categoryId,
  parentOptions,
  canManage,
  onClose,
  onResult,
}: CategoryFormDialogProps) {
  const { t } = useTranslation('catalog');
  const isEdit = categoryId !== null;

  const detailQuery = useCategory(categoryId ?? undefined, { enabled: open && isEdit });
  const create = useCreateCategory();
  const upsert = useUpsertCategory();

  const [slug, setSlug] = useState('');
  const [code, setCode] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [parentCategoryId, setParentCategoryId] = useState<string>(NO_PARENT_VALUE);
  const [slugTouched, setSlugTouched] = useState(false);

  // ── Card-language switcher: per-locale name/description drafts ─────────────────────────────
  const languagesQuery = useCatalogLanguages({ enabled: open });
  const languages = languagesQuery.data ?? [];
  const cardLang = useCardLanguageDraft<CategoryLocaleFields>(languages, CATEGORY_LOCALE_EMPTY_FIELDS);
  // Destructured once so the effects below depend on the specific stable functions/values they
  // use, not the whole `cardLang` object (a fresh reference every render).
  const { defaultLocale: cardDefaultLocale, seedField: cardSeedField, resetAll: cardResetAll } = cardLang;
  const translationsQuery = useCategoryTranslations(categoryId ?? undefined, {
    enabled: open && isEdit && languages.length > 0,
  });
  const editingDefaultLocale = cardLang.selectedLocale === cardDefaultLocale;

  useEffect(() => {
    if (!open) return;
    if (!isEdit) {
      setSlug('');
      setCode('');
      setImageUrl('');
      setParentCategoryId(NO_PARENT_VALUE);
      setSlugTouched(false);
      cardResetAll();
      return;
    }
    if (detailQuery.data) {
      const category = detailQuery.data;
      setSlug(category.slug ?? '');
      setCode(category.code ?? '');
      setImageUrl(category.imageUrl ?? '');
      setParentCategoryId(category.parentCategoryId ?? NO_PARENT_VALUE);
      setSlugTouched(true);
    }
  }, [open, isEdit, detailQuery.data, cardResetAll]);

  // Reset the per-locale drafts once per dialog OPEN in edit mode too (the create branch above
  // already resets on every open) — otherwise a previous category's drafts could leak into the
  // next one this same dialog instance edits (it stays mounted, toggling only `open`).
  useEffect(() => {
    if (open && isEdit) cardResetAll();
  }, [open, isEdit, categoryId, cardResetAll]);

  // The DEFAULT language's `name` prefills from the resolved category (the reference label) — the
  // category read model has no flat `description`, so BOTH fields for EVERY locale (including the
  // default) prefill from the dedicated translations endpoint. `seedField` is a per-(locale,field)
  // no-op once seeded, so arrival order between these two sources never clobbers an in-progress edit.
  useEffect(() => {
    // NOTE: `cardDefaultLocale` is `''` (a valid sentinel, not "not ready") in the empty-registry
    // single-language fallback — guard on `detailQuery.data` only, never on `cardDefaultLocale`'s
    // truthiness, or the fallback would never seed an existing category's name.
    if (!open || !isEdit || !detailQuery.data) return;
    cardSeedField(cardDefaultLocale, 'name', detailQuery.data.name ?? '');
  }, [open, isEdit, detailQuery.data, cardDefaultLocale, cardSeedField]);

  useEffect(() => {
    const stored = translationsQuery.data;
    if (!stored) return;
    for (const entry of stored) {
      cardSeedField(entry.locale, 'name', entry.name ?? '');
      cardSeedField(entry.locale, 'description', entry.description ?? '');
    }
  }, [translationsQuery.data, cardSeedField]);

  const selectedParentDepth = useMemo(
    () => parentOptions.find((option) => option.id === parentCategoryId)?.depth,
    [parentOptions, parentCategoryId],
  );
  const depthHintVisible = wouldLikelyExceedMaxDepth(selectedParentDepth);

  // The reference label (adr-catalog-006): the DEFAULT language's draft, regardless of which
  // locale the switcher currently shows — mirrors `ProductEditPage`'s `referenceFields`.
  const referenceFields = cardLang.fieldsFor(cardLang.defaultLocale);
  const valid = referenceFields.name.trim() !== '' && slug.trim() !== '';
  const loadingDetail = isEdit && detailQuery.isLoading;
  const detailFailed = isEdit && detailQuery.isError;
  const pending = create.isPending || upsert.isPending;

  /**
   * `translations[]` for the save payload — only the locales touched this session; a
   * touched-but-blanked name is dropped (the schema requires a non-empty `name` per entry).
   * `undefined` when the tenant has no configured languages or nothing was touched.
   */
  const buildTranslationsPayload = (): CategoryTranslationInput[] | undefined => {
    if (cardLang.enabledLanguages.length === 0) return undefined;
    const entries = cardLang
      .touchedEntries()
      .filter((entry) => entry.name.trim() !== '')
      .map((entry) => ({
        locale: entry.locale,
        name: entry.name.trim(),
        description: entry.description.trim() || undefined,
      }));
    return entries.length > 0 ? entries : undefined;
  };

  const submit = () => {
    if (!valid) return;
    const body: CategoryUpsertRequest = {
      // The reference label (adr-catalog-006): always the DEFAULT language's draft. Unlike
      // products, a category has no flat `description` — it travels ONLY via `translations[]`.
      name: referenceFields.name.trim(),
      slug: slug.trim(),
      code: code.trim() || undefined,
      imageUrl: imageUrl.trim() || undefined,
      productCount: detailQuery.data?.productCount,
      parentCategoryId: parentCategoryId || undefined,
      translations: buildTranslationsPayload(),
    };
    const handlers = {
      onSuccess: () => {
        onResult('success', isEdit ? t('categoryFormDialog.saved') : t('categoryFormDialog.created'));
        onClose();
      },
      onError: (error: NormalizedApiError) => {
        if (error.code === 'CATEGORY_CYCLE') {
          onResult('error', t('categoryFormDialog.errors.cycle'));
          return;
        }
        if (error.code === 'CATEGORY_DEPTH_EXCEEDED') {
          onResult('error', t('categoryFormDialog.errors.depthExceeded'));
          return;
        }
        onResult('error', error.message);
      },
    };
    if (isEdit && categoryId) {
      upsert.mutate({ categoryId, body }, handlers);
      return;
    }
    create.mutate(body, handlers);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEdit ? t('categoryFormDialog.editTitle') : t('categoryFormDialog.newTitle')}
      </DialogTitle>
      <DialogContent>
        {loadingDetail ? (
          <LoadingState label={t('categoryFormDialog.loading')} fullHeight={false} />
        ) : detailFailed ? (
          <ErrorState
            title={t('categoryFormDialog.loadError')}
            message={detailQuery.error?.message}
            onRetry={() => void detailQuery.refetch()}
          />
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {/* Card-language switcher (epic-catalog-004, adr-tenant-005) — hidden automatically
                when the tenant has no configured catalog languages (single-language fallback). */}
            <Stack spacing={1}>
              <CardLanguageSwitcher
                languages={cardLang.enabledLanguages}
                selectedLocale={cardLang.selectedLocale}
                onSelect={cardLang.selectLocale}
                isFilled={cardLang.isFilled}
                disabled={!canManage}
              />
              {cardLang.enabledLanguages.length > 0 && cardLang.isDirty ? (
                <Alert severity="info" variant="outlined" sx={{ py: 0.25 }}>
                  {t('categoryFormDialog.cardLanguage.unsavedHint')}
                </Alert>
              ) : null}
            </Stack>
            <TextField
              label={
                cardLang.enabledLanguages.length > 0
                  ? `${t('categoryFormDialog.nameLabel')} — ${
                      cardLang.enabledLanguages.find((l) => l.locale === cardLang.selectedLocale)?.name ??
                      cardLang.selectedLocale
                    }`
                  : t('categoryFormDialog.nameLabel')
              }
              required={editingDefaultLocale}
              value={cardLang.fields.name}
              onChange={(e) => {
                cardLang.setField('name', e.target.value);
                // The slug technical identifier is derived from the REFERENCE (default-language)
                // name only — typing a non-default translation must never perturb it.
                if (editingDefaultLocale && !slugTouched) setSlug(slugify(e.target.value));
              }}
              error={editingDefaultLocale && cardLang.fields.name.trim() === ''}
              autoFocus
              fullWidth
              disabled={!canManage}
            />
            <TextField
              label={
                cardLang.enabledLanguages.length > 0
                  ? `${t('categoryFormDialog.descriptionLabel')} — ${
                      cardLang.enabledLanguages.find((l) => l.locale === cardLang.selectedLocale)?.name ??
                      cardLang.selectedLocale
                    }`
                  : t('categoryFormDialog.descriptionLabel')
              }
              value={cardLang.fields.description}
              onChange={(e) => cardLang.setField('description', e.target.value)}
              fullWidth
              multiline
              minRows={2}
              disabled={!canManage}
            />
            <TextField
              label={t('categoryFormDialog.slugLabel')}
              required
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugTouched(true);
              }}
              error={slug.trim() === ''}
              fullWidth
              disabled={!canManage}
              slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace' } } }}
            />
            <TextField
              label={t('categoryFormDialog.codeLabel')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              fullWidth
              disabled={!canManage}
            />
            <TextField
              select
              label={t('categoryFormDialog.parentLabel')}
              value={parentCategoryId}
              onChange={(e) => setParentCategoryId(e.target.value)}
              helperText={t('categoryFormDialog.parentHelper')}
              fullWidth
              disabled={!canManage}
            >
              <MenuItem value={NO_PARENT_VALUE}>{t('categoryFormDialog.parentNone')}</MenuItem>
              {parentOptions.map((option) => (
                <MenuItem
                  key={option.id}
                  value={option.id}
                  sx={{ pl: 2 + option.depth * 2 }}
                >
                  {'— '.repeat(option.depth)}
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
            {depthHintVisible ? (
              <Alert severity="warning" variant="outlined">
                {t('categoryFormDialog.depthHint')}
              </Alert>
            ) : null}
            <TextField
              label={t('categoryFormDialog.imageUrlLabel')}
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              fullWidth
              disabled={!canManage}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!valid || pending || loadingDetail || detailFailed || !canManage}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
